import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SubscriptionStatus from '../components/SubscriptionStatus';
import SubscriptionCheckout from '../components/SubscriptionCheckout';

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/subscriptions/status', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) setStatus(await res.json());
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchStatus();
  }, [user]);

  // Handle redirect after successful payment
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      setMessage('✅ Subscription activated! Welcome to Premium.');
      setTimeout(() => window.location.reload(), 2000);
    } else if (status === 'cancelled') {
      setMessage('⚠️ Subscription process cancelled. Try again when ready.');
    }
  }, [searchParams]);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure? Your subscription will end at the end of the current billing period.')) {
      return;
    }

    setCancelling(true);
    try {
      const res = await fetch('/api/subscriptions/cancel', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        setMessage('✅ Subscription cancelled. You can resubscribe anytime.');
        setTimeout(() => window.location.reload(), 2000);
      } else {
        const data = await res.json();
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) {
      setMessage('❌ Failed to cancel subscription');
      console.error(err);
    } finally {
      setCancelling(false);
    }
  };

  if (!user) return null;
  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>;

  const isPremium = status?.hasSubscription && status?.planType === 'premium';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 24 }}>Subscription & Billing</h1>

      {message && (
        <div style={{
          padding: 16,
          background: message.startsWith('✅') ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
          border: `1px solid ${message.startsWith('✅') ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
          borderRadius: 12,
          color: message.startsWith('✅') ? '#2e7d32' : '#e65100',
          marginBottom: 24,
          fontSize: 14,
          fontWeight: 500
        }}>
          {message}
        </div>
      )}

      <SubscriptionStatus />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginBottom: 24 }}>
        {/* Current Plan */}
        <div style={{
          padding: 16,
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: 'white'
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
            {isPremium ? '⭐ Premium Plan' : '🆓 Free Plan'}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.6 }}>
            <div style={{ marginBottom: 12 }}>
              <strong>Price:</strong> {isPremium ? '$25/month' : 'Free'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Status:</strong> {isPremium ? '✅ Active' : 'Inactive'}
            </div>
            <div>
              <strong>Features:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 16 }}>
                <li>✅ {isPremium ? 'Unlimited' : '3'} listings</li>
                <li>✅ All listing types</li>
                {isPremium && <li>✅ Priority support</li>}
              </ul>
            </div>
          </div>

          {isPremium && status?.subscription?.current_period_end && (
            <div style={{
              marginTop: 16,
              padding: 12,
              background: 'var(--cream)',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--text-soft)'
            }}>
              <strong>Next billing date:</strong>{' '}
              {new Date(status.subscription.current_period_end).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          padding: 16,
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: 'white'
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!isPremium && (
              <button
                onClick={() => document.querySelector('[style*="SubscriptionCheckout"]')?.scrollIntoView({ behavior: 'smooth' })}
                style={{
                  padding: '10px 16px',
                  background: 'var(--cherry)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity .2s',
                  textAlign: 'left'
                }}
                onMouseEnter={e => e.target.style.opacity = '0.9'}
                onMouseLeave={e => e.target.style.opacity = '1'}
              >
                🚀 Upgrade to Premium
              </button>
            )}

            {isPremium && (
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                style={{
                  padding: '10px 16px',
                  background: 'white',
                  color: '#d32f2f',
                  border: '1px solid #d32f2f',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: cancelling ? 'not-allowed' : 'pointer',
                  opacity: cancelling ? 0.6 : 1
                }}
              >
                {cancelling ? 'Cancelling...' : '❌ Cancel Subscription'}
              </button>
            )}

            <button
              onClick={() => navigate('/dashboard/listings')}
              style={{
                padding: '10px 16px',
                background: 'var(--cream)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background .2s',
                textAlign: 'left'
              }}
              onMouseEnter={e => e.target.style.background = 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.target.style.background = 'var(--cream)'}
            >
              📋 View My Listings
            </button>
          </div>
        </div>
      </div>

      {!isPremium && (
        <div style={{ marginBottom: 24 }}>
          <SubscriptionCheckout />
        </div>
      )}

      {/* FAQ */}
      <div style={{
        padding: 24,
        borderRadius: 12,
        border: '1px solid var(--border)',
        background: 'white'
      }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>FAQ</h2>
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>What's the difference between Free and Premium?</strong>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-soft)' }}>
              Free tier includes 3 listings (sales, rentals, auctions, barter). Premium ($25/month) offers unlimited listings with all features.
            </p>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>Can I cancel anytime?</strong>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-soft)' }}>
              Yes! Your subscription cancels at the end of your current billing period. No hidden fees.
            </p>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>Are free items always free to list?</strong>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-soft)' }}>
              Yes! Free items don't count toward your listing limit, whether you're on Free or Premium tier.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
